import { Skeleton } from '@mui/material';
import { isEqual, set } from 'lodash';
import { lazy, memo, Suspense, useRef } from 'react';

import { GCFErrorBoundary } from '../errors/GCFErrorBoundary';
import { GCFConfigProvider } from '../gcfConfigProvider';
import { FullGcfComponentSectionProps } from './FullGcfComponentSection.interface';
import { ContentWrapper, Wrapper } from './FullGcfComponentSection.styled';

const Section = lazy(() =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.Section,
  })),
);

const MemoizedSection = memo(
  (props: any) => {
    return <Section {...props} />;
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);

export const FullGcfComponentSection = memo(
  ({
    screenSectionId,
    assemblyComponents,
    height,
    width,
    previewData,
    containerSize,
    containerStyle,
    overrideQuerySetting,
    hidden,
    isFromSidebar,
    productFilters,
    productVariationFilters,
    skipProductsApiOnEmptyFilters,
    overflow,
  }: FullGcfComponentSectionProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const updatedAssemblyComponents = assemblyComponents.map((data) => {
      if (!data?.component?.theme) {
        set(
          data,
          'component.theme',
          data.component?.config?.scaleTypeKey ??
            data.component?.config?.themeKey ??
            'default',
        );
      }

      return data;
    });

    return (
      <Wrapper
        $hidden={hidden}
        $overflow={overflow}
        ref={containerRef}
        sx={containerStyle}>
        {!containerSize?.isResizing && (
          <ContentWrapper width={width} height={height}>
            <GCFErrorBoundary>
              <Suspense
                fallback={
                  <Skeleton height="100%" width="100%" variant="rectangular" />
                }>
                <GCFConfigProvider
                  screenSectionId={screenSectionId}
                  overrideQuerySetting={overrideQuerySetting}
                  productFilters={productFilters}
                  productVariationFilters={productVariationFilters}
                  skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}>
                  <MemoizedSection
                    id={screenSectionId}
                    top="0"
                    left="0"
                    height={height}
                    width={width}
                    previewData={previewData}
                    assemblyComponents={updatedAssemblyComponents}
                    isFromSidebar={isFromSidebar}
                  />
                </GCFConfigProvider>
              </Suspense>
            </GCFErrorBoundary>
          </ContentWrapper>
        )}
      </Wrapper>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);
